import {skipToken} from '@reduxjs/toolkit/query';

import {useState} from 'react';

import {equals, isNil} from 'ramda';

import type {
  BulkOperationResponseBody,
  DataGridBulkActionsResponseBody,
} from '../types/api/bulkOperations';
import type {BulkOperationsServices} from '../types/BulkOperationsServices';

const PAGE_SIZE = 100;
const MAX_HISTORY_PAGES = 100;

type HistoryState = {
  scope: string;
  offset: number;
  pagesLoaded: number;
  operations: BulkOperationResponseBody[];
  snapshot?: BulkOperationResponseBody[];
  complete: boolean;
  request?: {offset: number; cachedRequestId?: string};
};

function createHistory(scope: string): HistoryState {
  return {scope, offset: 0, pagesLoaded: 0, operations: [], complete: false};
}

/** Load raw history in bounded batches, before grid-support and dismissal filtering. */
export function useBulkOperationHistory(
  services: BulkOperationsServices,
  discovery: DataGridBulkActionsResponseBody | undefined
) {
  const filters = isNil(discovery)
    ? undefined
    : {
        resourceId: discovery.resourceId,
        actionCode: discovery.actions.map((action) => action.actionCode),
      };
  const scope = JSON.stringify(filters ?? null);
  const [state, setState] = useState(() => createHistory(scope));
  let accepted = state.scope === scope ? state : createHistory(scope);
  const enabled = !isNil(filters) && filters.actionCode.length > 0;
  const isPaused = !accepted.complete && accepted.pagesLoaded >= MAX_HISTORY_PAGES;
  const query = services.useListOperations(
    enabled && !isPaused
      ? {
          ...filters,
          limit: PAGE_SIZE,
          offset: accepted.offset,
        }
      : skipToken,
    {skip: !enabled || isPaused, refetchOnMountOrArgChange: true}
  );

  if (enabled && accepted.request?.offset !== accepted.offset) {
    accepted = {
      ...accepted,
      request: {
        offset: accepted.offset,
        cachedRequestId: query.isFetching ? undefined : query.requestId,
      },
    };
  }
  // A cached result precedes RTK Query's mount-time refetch. Let that subscription commit
  // and finish a new request before moving to the next page, including on preset remounts.
  const isFreshRequest =
    isNil(accepted.request?.cachedRequestId) ||
    query.requestId !== accepted.request.cachedRequestId;

  if (
    enabled &&
    !isPaused &&
    isFreshRequest &&
    !query.isFetching &&
    !query.isError &&
    !isNil(query.currentData) &&
    !equals(query.currentData, accepted.snapshot)
  ) {
    const page = query.currentData;
    const operations = new Map(
      accepted.operations.map((operation) => [operation.bulkOperationId, operation])
    );
    const previousSize = operations.size;
    page.forEach((operation) => operations.set(operation.bulkOperationId, operation));
    const complete = page.length < PAGE_SIZE || operations.size === previousSize;
    accepted = {
      ...accepted,
      pagesLoaded: accepted.pagesLoaded + 1,
      operations: [...operations.values()],
      complete,
      offset: complete ? accepted.offset : accepted.offset + page.length,
      snapshot: complete ? page : undefined,
    };
  }
  if (accepted !== state) {
    setState(accepted);
  }
  const limitReached = !accepted.complete && accepted.pagesLoaded >= MAX_HISTORY_PAGES;

  return {
    operations: accepted.operations,
    isLoading: enabled && !accepted.complete && !limitReached && !query.isError,
    isError: enabled && (query.isError || limitReached),
    retry: () => {
      if (limitReached) {
        setState((current) => ({...current, pagesLoaded: 0}));
      } else {
        query.refetch?.();
      }
    },
  };
}
